import { useEffect, useState } from 'react';
import { format, startOfMonth, subMonths, endOfMonth } from 'date-fns';
import { Input } from './ui/input';
import { Button } from './ui/button';

const chf = (v) => `CHF ${Number(v || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ymd = (d) => format(d, 'yyyy-MM-dd');

// Head-coach only: paid academy training revenue + coach hours for a period.
export const HeadCoachStats = ({ API, L }) => {
  const today = new Date();
  const [range, setRange] = useState({ start: ymd(startOfMonth(today)), end: ymd(today) });
  const [preset, setPreset] = useState('mtd');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!range.start || !range.end || range.start > range.end) return;
    setLoading(true);
    API.get(`/coach/academy-stats?start_date=${range.start}&end_date=${range.end}`)
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [range, API]);

  const pick = (p) => {
    setPreset(p);
    if (p === 'mtd') setRange({ start: ymd(startOfMonth(today)), end: ymd(today) });
    if (p === 'last') { const lm = subMonths(today, 1); setRange({ start: ymd(startOfMonth(lm)), end: ymd(endOfMonth(lm)) }); }
  };
  const types = [
    ['private', L('Private lessons', 'Privatlektionen')], ['group', L('Group trainings', 'Gruppentrainings')],
    ['academy', L('Junior / Adult Academy', 'Junior / Adult Academy')], ['abos', L('Training Abos sold', 'Training-Abos verkauft')],
    ['other', L('Other academy', 'Sonstiges Academy')],
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5" data-testid="head-coach-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-black text-slate-900">{L('Academy numbers', 'Academy Zahlen')}</h3>
        <div className="flex flex-wrap gap-2 items-center">
          {[['mtd', L('Month to date', 'Monat bis heute')], ['last', L('Last month', 'Letzter Monat')], ['custom', L('Custom', 'Zeitraum')]].map(([k, lbl]) => (
            <Button key={k} size="sm" variant={preset === k ? 'default' : 'outline'} onClick={() => pick(k)}
              className={preset === k ? 'bg-emerald-500 hover:bg-emerald-600 text-slate-900' : ''} data-testid={`hc-preset-${k}`}>{lbl}</Button>
          ))}
        </div>
      </div>
      {preset === 'custom' && (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <Input type="date" value={range.start} onChange={(e) => setRange(r => ({ ...r, start: e.target.value }))} className="w-auto" data-testid="hc-start" />
          <span>–</span>
          <Input type="date" value={range.end} onChange={(e) => setRange(r => ({ ...r, end: e.target.value }))} className="w-auto" data-testid="hc-end" />
        </div>
      )}
      {loading && !data && <p className="text-sm text-slate-500">{L('Loading…', 'Lädt…')}</p>}
      {data && (
        <div className={`grid gap-5 md:grid-cols-2 ${loading ? 'opacity-60' : ''}`}>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{L('Training revenue (paid)', 'Trainings-Umsatz (bezahlt)')}</p>
            <p className="text-3xl font-black text-slate-900" data-testid="hc-revenue-total">{chf(data.revenue_total)}</p>
            <div className="divide-y divide-slate-100 text-sm">
              {types.map(([k, lbl]) => (
                <div key={k} className="flex justify-between py-1.5" data-testid={`hc-rev-${k}`}>
                  <span className="text-slate-600">{lbl}</span><span className="font-semibold">{chf(data.revenue_by_type[k])}</span>
                </div>
              ))}
              {data.refunds < 0 && (
                <div className="flex justify-between py-1.5 text-red-600 text-xs" data-testid="hc-refunds">
                  <span>{L('incl. refunds', 'inkl. Rückerstattungen')}</span><span>{chf(data.refunds)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{L('Coach hours', 'Trainer-Stunden')}</p>
            <p className="text-3xl font-black text-slate-900" data-testid="hc-hours-total">{data.total_hours.toFixed(1)} h</p>
            <div className="divide-y divide-slate-100 text-sm">
              {data.coaches.map(c => (
                <div key={c.trainer_id} className="flex justify-between py-1.5" data-testid={`hc-coach-${c.trainer_id}`}>
                  <span className="text-slate-600">{c.name}</span>
                  <span className="font-semibold">{c.hours.toFixed(1)} h <span className="text-slate-400 font-normal">· {c.lessons} {L('lessons', 'Lektionen')}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
